// Builders that turn protobuf messages into the plain view data of the HUD.

import type {
  BotLevel,
  HudMode,
  KillfeedEntry,
  MatchEndView,
  RewardsView,
  ScoreboardRow,
  ScoreboardView,
  TeamTag,
} from "../hud/types";
import { TICK_RATE } from "../net/constants";
import type { Killed, MatchEnded, Rewards, ScoreEntry } from "../proto/gunrun/v1/game_pb";
import { teamTag } from "./convert";

/** What the builders need to know about a player of the room. */
export interface RosterEntry {
  name: string;
  team: TeamTag;
  alive: boolean;
  /** Set for a bot. */
  bot?: BotLevel | null;
}

export type RosterLookup = (id: number) => RosterEntry | null;

/** Team names as the HUD shows them: sides in Defusal, colors elsewhere. */
export function teamName(mode: HudMode, team: TeamTag): string {
  if (team === "none") return "";
  if (mode === "defusal") return team === "a" ? "Attackers" : "Defenders";
  return team === "a" ? "Orange" : "Blue";
}

/** "Orange wins 75 - 60", "Defenders win 7 - 5". */
function winLine(mode: HudMode, team: TeamTag, scores: [number, number] | null): string {
  const verb = mode === "defusal" ? "win" : "wins";
  return `${teamName(mode, team)} ${verb}${scores ? ` ${scores[0]} - ${scores[1]}` : ""}`;
}

export function killfeedEntry(k: Killed, roster: RosterLookup, myId: number): KillfeedEntry {
  const killerId = k.killer;
  const killer = killerId === undefined ? null : roster(killerId);
  const victim = roster(k.victim);
  const src = k.source?.source;
  let weapon = "world";
  if (src?.case === "weapon" || src?.case === "equipment") weapon = src.value;
  else if (src?.case === "bomb") weapon = "bomb";
  else if (src?.case === "suicide") weapon = "suicide";
  const tags: string[] = [];
  if (k.noscope) tags.push("noscope");
  if (k.midair) tags.push("midair");
  if (k.wallrun) tags.push("wallrun");
  if (k.longshot) tags.push("longshot");
  if (k.sliding) tags.push("sliding");
  return {
    killer: killer ? killer.name : null,
    killerTeam: killer ? killer.team : "none",
    victim: victim ? victim.name : `Player ${k.victim}`,
    victimTeam: victim ? victim.team : "none",
    weapon,
    headshot: k.headshot,
    tags,
    mine: k.victim === myId || killerId === myId,
  };
}

export function scoreboardRows(
  entries: readonly ScoreEntry[],
  roster: RosterLookup,
  myId: number,
  rows: ScoreboardRow[] = [],
): ScoreboardRow[] {
  rows.length = 0;
  for (const e of entries) {
    const p = roster(e.id);
    rows.push({
      id: e.id,
      name: p ? p.name : `Player ${e.id}`,
      bot: p?.bot ?? null,
      team: p ? p.team : "none",
      kills: e.kills,
      deaths: e.deaths,
      assists: e.assists,
      score: e.score,
      objective: e.objective,
      pingMs: e.pingMs,
      isMe: e.id === myId,
      alive: p ? p.alive : false,
    });
  }
  return rows;
}

export function buildScoreboard(
  mode: HudMode,
  title: string,
  rows: ScoreboardRow[],
  teamScores: [number, number] | null,
): ScoreboardView {
  return { mode, title, rows, teamScores };
}

/** Highest-scoring row (leader of an FFA / the player to beat). */
export function leaderOf(rows: readonly ScoreboardRow[]): ScoreboardRow | null {
  let best: ScoreboardRow | null = null;
  for (const r of rows) if (!best || r.score > best.score) best = r;
  return best;
}

/** Seconds until `endTick`, null when the phase has no timer (end tick 0). */
export function secondsLeft(endTick: number, nowTick: number): number | null {
  if (endTick <= 0) return null;
  return Math.max(0, (endTick - nowTick) / TICK_RATE);
}

export function matchEndView(
  m: MatchEnded,
  mode: HudMode,
  roster: RosterLookup,
  myId: number,
  myTeam: TeamTag,
): MatchEndView {
  const rows = scoreboardRows(m.entries, roster, myId);
  rows.sort((a, b) => b.score - a.score || b.kills - a.kills);
  const teamMode = mode !== "ffa";
  const scores: [number, number] | null = teamMode && m.scores ? [m.scores.a, m.scores.b] : null;
  let title: string;
  let subtitle: string;
  let tone: "good" | "bad" | "neutral";
  if (teamMode) {
    const winner = m.winnerTeam === undefined ? "none" : teamTag(m.winnerTeam);
    if (winner === "none") {
      title = "DRAW";
      subtitle = scores ? `${scores[0]} - ${scores[1]}` : "";
      tone = "neutral";
    } else if (winner === myTeam) {
      title = "VICTORY";
      subtitle = winLine(mode, winner, scores);
      tone = "good";
    } else {
      title = "DEFEAT";
      subtitle = winLine(mode, winner, scores);
      tone = "bad";
    }
  } else {
    const winnerId = m.winnerPlayer;
    const winner = winnerId === undefined ? null : roster(winnerId);
    if (winnerId === myId) {
      title = "VICTORY";
      subtitle = "You finished first";
      tone = "good";
    } else {
      title = winner ? "MATCH OVER" : "DRAW";
      subtitle = winner ? `${winner.name} wins` : "";
      tone = "neutral";
    }
  }
  const mvp = m.mvp === undefined ? null : roster(m.mvp);
  return {
    title,
    subtitle,
    tone,
    rows,
    teamScores: scores,
    mvpName: mvp ? mvp.name : null,
    nextInSec: m.nextInSec > 0 ? m.nextInSec : null,
    rewards: null,
  };
}

/** The server's `Rewards` as HUD view data. */
export function rewardsView(r: Rewards): RewardsView {
  const need = r.xpIntoLevel + r.xpForNext;
  return {
    xpItems: r.xpItems.map((i) => ({ label: i.label, xp: i.xp })),
    xpTotal: r.xpTotal,
    levelBefore: r.levelBefore,
    levelAfter: r.levelAfter,
    leveledUp: r.levelAfter > r.levelBefore,
    xpIntoLevel: r.xpIntoLevel,
    xpForNext: r.xpForNext,
    // `xpForNext` is 0 at the maximum level: the bar stays full.
    levelProgress: r.xpForNext === 0 ? 1 : need > 0 ? r.xpIntoLevel / need : 0,
    unlocked: [...r.unlocked],
    weaponLevels: r.weaponLevels.map((w) => ({
      weapon: w.weapon,
      xpGained: w.xpGained,
      levelBefore: w.levelBefore,
      levelAfter: w.levelAfter,
      leveledUp: w.levelAfter > w.levelBefore,
    })),
    rating: r.rating,
    ratingDelta: r.ratingDelta ?? null,
  };
}

/**
 * `view` with the rewards attached, `elapsedSec` seconds after it was first shown: the next
 * match countdown keeps running instead of starting over when the overlay is shown again.
 */
export function withRewards(view: MatchEndView, rewards: RewardsView, elapsedSec: number): MatchEndView {
  const next = view.nextInSec === null ? null : Math.max(0, view.nextInSec - elapsedSec);
  return { ...view, rewards, nextInSec: next };
}
